import { Button, ItemGroup, TreeList } from '@ibsys/kit';
import { NetworkIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeResponse } from '@/entities/attributes';
import { cn, useAppNavigate } from '@/shared/lib';
import { useParams, useSearchParams } from 'react-router-dom';
import { AttributeCard } from './AttributeCard';

type Props = {
  structure: AttributeResponse;
  attributes: AttributeResponse[];
};

const ICON_SIZE = 18;

export const StructureGroup: FC<Props> = ({ attributes, structure }) => {
  const { t } = useTranslation('attributes');
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useAppNavigate();

  const onEditStructureClick = () => {
    navigate(`/attributes/:id/?${searchParams.toString()}`, {
      id: structure.code,
    });
  };

  const activeParentStructCode = searchParams.get('parentStructCode');

  const isActive = (attribute: AttributeResponse) => {
    return attribute.code === id && (activeParentStructCode || 'SIMPLE') === attribute.parentStructCode;
  };

  return (
    <TreeList.Group
      value={`${structure.parentStructCode}.${structure.code}`}
      classNames={{
        content: 'p-3',
      }}
      label={
        <div className="flex flex-1 items-start gap-2">
          <div className={cn('mt-0.5 shrink-0', 'text-muted-foreground group-hover:text-foreground')}>
            <NetworkIcon size={ICON_SIZE} />
          </div>
          <div className="flex-1 flex">
            <span className="line-clamp-2 text-left text-sm font-medium leading-tight">{structure.name}</span>
            {attributes.length !== undefined && (
              <span className="ml-2 text-xs text-muted-foreground">{`(${attributes.length})`}</span>
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        {attributes.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">{t('noAttributes')}</div>
        ) : (
          <ItemGroup>
            <Button variant={'ghost'} size={'sm'} onClick={onEditStructureClick} className={'justify-start mb-3'}>
              {t('editStructure')}
            </Button>
            <p className={'text-sm text-muted-foreground mb-1'}>{`${t('structureAttributes')}:`}</p>
            <div className="flex flex-col gap-4">
              {attributes.map((attribute) => (
                <AttributeCard
                  key={`${attribute.code}.${attribute.parentStructCode}`}
                  attribute={attribute}
                  isActive={isActive(attribute)}
                />
              ))}
            </div>
          </ItemGroup>
        )}
      </div>
    </TreeList.Group>
  );
};
